import React from "react";
import { Link } from "react-router-dom";
import { Check, Heart, Leaf, Moon, Play, Sparkles, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useJoinCourse } from "@/lib/useJoinCourse";
import YogaGurus from "@/components/yoga/YogaGurus";
import YogaCourses from "@/components/yoga/YogaCourses";

const HERO_IMG = "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1400&q=85";
const GATHERING_IMGS = [
  "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=1000&q=80",
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1000&q=80",
  "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1000&q=80",
];

const INTRO_PRICE = 100;
const MONTHLY_PRICE = 251;
const INTRO_DAYS = 21;

const bullets = [
  { icon: Sun,      title: "10-25 min sessions",   sub: "Bite-sized flows that fit any morning." },
  { icon: Heart,    title: "Beginner-first",       sub: "No prior yoga experience required." },
  { icon: Leaf,     title: "Progressive path",     sub: "Each day unlocks after the previous is done." },
  { icon: Moon,     title: "Live Fri/Sat classes", sub: "Community practice with the teacher." },
];

const days = Array.from({ length: 21 }, (_, i) => i + 1);

export default function YogaLanding() {
  const { join, joining } = useJoinCourse();

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden bg-paper">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center px-6 lg:px-12 py-16 lg:py-24">
          <div className="lg:col-span-7 fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs tracking-widest uppercase font-medium mb-6">
              <Sparkles size={12}/> 21-Day Mild Yoga Starter
            </div>
            <h1 className="font-display text-5xl lg:text-7xl leading-[1.02] tracking-tight text-foreground">
              Slow down.<br/>Breathe deeper.<br/>
              <span className="text-secondary italic">Start for ₹{INTRO_PRICE}.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A gentle 21-day path designed for absolute beginners, taught by certified trainers and doctors.
              ₹{INTRO_PRICE} covers your first {INTRO_DAYS} days — after that it continues at ₹{MONTHLY_PRICE}/month,
              cancel whenever you like.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={join} disabled={joining} className="btn-action px-7 h-12" data-testid="yoga-start-btn">
                <Play size={16} className="mr-2 fill-current" /> {joining ? "Opening…" : `Join Course — ₹${INTRO_PRICE}`}
              </Button>
              <Link to="/yoga/dashboard">
                <Button variant="outline" className="btn-pill border-primary/30 text-primary hover:bg-accent h-12 px-7" data-testid="yoga-dashboard-link">
                  I'm already a member
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-8 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check size={14} className="text-primary"/> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-primary"/> No prior experience</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-primary"/> Watch on any device</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-[2.5rem] overflow-hidden border border-border shadow-lg aspect-[4/5]">
              <img src={HERO_IMG} alt="Yoga" className="w-full h-full object-cover" />
              <div className="absolute inset-x-6 bottom-6 bg-background/85 backdrop-blur rounded-2xl p-4 border border-border">
                <div className="text-[10px] uppercase tracking-widest text-secondary mb-1">Today's practice</div>
                <div className="font-display text-xl">Gentle Awakening — 15 min</div>
                <div className="text-xs text-muted-foreground mt-0.5">Day 2 of 21 · Beginner</div>
              </div>
            </div>
          </div>
        </div>

        {/* Posture & gathering imagery */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-16 grid sm:grid-cols-3 gap-4" data-testid="yoga-gallery">
          {GATHERING_IMGS.map((src, i) => (
            <div key={src} className="rounded-3xl overflow-hidden border border-border aspect-[5/3]">
              <img
                src={src}
                alt="Yogic posture and group practice"
                loading="lazy"
                className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700"
                data-testid={`yoga-gallery-img-${i}`}
              />
            </div>
          ))}
        </div>
      </section>

      <YogaGurus />

      <YogaCourses onJoin={join} joining={joining} cta={`Join — ₹${INTRO_PRICE}`} />

      {/* FEATURES */}
      <section className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bullets.map((b) => (
              <div key={b.title} className="border border-border rounded-2xl p-6 hover:border-primary/40 transition-all">
                <b.icon className="text-secondary mb-5" size={26}/>
                <h3 className="font-display text-2xl mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATH PREVIEW */}
      <section className="border-t border-border bg-accent/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="overline mb-3">The 21-Day Path</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-3">One gentle step at a time.</h2>
            <p className="text-muted-foreground">Each day is unlocked when you complete the previous. No skipping — the body needs its rhythm.</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-3">
            {days.map((d) => (
              <div key={d} className={`aspect-square rounded-xl border ${d === 1 ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"} flex flex-col items-center justify-center transition-transform hover:scale-105`}>
                <div className="text-[10px] uppercase tracking-widest opacity-60">Day</div>
                <div className="font-display text-3xl">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20 text-center">
          <div className="overline mb-3">Simple, transparent</div>
          <h2 className="font-display text-4xl lg:text-5xl mb-3">₹{INTRO_PRICE} for your first {INTRO_DAYS} days</h2>
          <p className="text-muted-foreground mb-10">
            ₹{INTRO_PRICE} unlocks all {INTRO_DAYS} days, the live weekend classes and every course. It then
            continues automatically at ₹{MONTHLY_PRICE}/month — the same rate for every renewal.
          </p>

          <div className="border-2 border-primary/20 rounded-3xl p-8 lg:p-10 bg-card relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-secondary/10 text-secondary text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">Most popular</div>
            <div className="font-display text-6xl mb-2">₹{INTRO_PRICE}</div>
            <div className="text-sm text-muted-foreground mb-8">for your first {INTRO_DAYS} days · then ₹{MONTHLY_PRICE}/month</div>
            <ul className="text-sm text-left max-w-sm mx-auto space-y-2 mb-8">
              {[
                "All 21 daily video sessions",
                "Beginner, Prana Healing, Pranayam & Meditation courses",
                "1-on-1 guru session, 7:00 – 8:00 PM by appointment",
                "Live Friday & Saturday classes",
                "Progress tracker & unlockable path",
                "Practice on any device",
                "Cancel anytime — no lock-in",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check size={14} className="text-primary"/> {f}</li>
              ))}
            </ul>
            <Button onClick={join} disabled={joining} className="btn-action px-10 h-12" data-testid="yoga-cta-btn">
              {joining ? "Opening payment…" : "Join Course"}
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
        <div className="leaf-divider max-w-xs mx-auto mb-4"><Leaf size={14}/></div>
        © {new Date().getFullYear()} Immortal Vedas · 21-Day Mild Yoga
      </footer>
    </main>
  );
}
